import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { identify } from '@/lib/analytics';
import { TOKEN_KEY, USER_KEY, readToken } from '@/api/axiosInstance';

const AuthContext = createContext({
  isAuthModalOpen: false,
  openAuthModal: () => {},
  closeAuthModal: () => {},
  user: null,
  login: (userData) => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState(null);

  // Load user from localStorage on initial render
  useEffect(() => {
    // `readToken` rejects the strings "undefined"/"null", which earlier builds
    // could leave behind. Those are truthy, so a plain presence check restored
    // the user as signed in with a credential the API would reject — the state
    // that surfaced a raw "Unauthorized access" at checkout. Treat it as signed
    // out and clear it, so the visitor simply logs in again.
    const token = readToken();
    const userData = localStorage.getItem(USER_KEY);

    if (!token && userData) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return;
    }

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        // A returning visitor is restored from storage without passing through
        // `login`, so without this their whole session would be attributed to a
        // guest.
        identify(parsedUser?.id);
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Clear invalid data
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
  }, []);

  // axiosInstance clears the stored credential when the API rejects it and
  // announces it here, so a token the server no longer accepts ends in a login
  // prompt rather than a bare "Unauthorized" the user cannot act on.
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setIsAuthModalOpen(true);
    };
    window.addEventListener('lk:unauthorized', onUnauthorized);
    return () => window.removeEventListener('lk:unauthorized', onUnauthorized);
  }, []);

  const openAuthModal = useCallback(() => {
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const login = useCallback((userData) => {
    // Ensure we have all required user data
    const userWithDefaults = {
      ...userData,
      name: userData.name || `+91-${userData.phone || ''}`,
      phone: userData.phone || ''
    };
    setUser(userWithDefaults);
    // Only write a token we actually have. `localStorage` stringifies whatever
    // it is given, so passing `undefined` here used to store the literal string
    // "undefined" — silently destroying a perfectly good session. Any caller
    // rehydrating a stored user (which carries no token) would wipe the real
    // credential on every page load, and the next protected call came back
    // 401 "Unauthorized access" while the UI still showed the user signed in.
    if (userData.token) {
      localStorage.setItem(TOKEN_KEY, userData.token);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(userWithDefaults));
    // Attach this browser's analytics to the customer. Events recorded while
    // they were a guest keep their null userId; the backend links the device to
    // the user, so the guest trail stays reachable without rewriting history.
    identify(userWithDefaults.id);
    setIsAuthModalOpen(false);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Stop attributing this browser's events to them, but keep the install id:
    // it is the same visitor, now anonymous again.
    identify(null);
    setIsAuthModalOpen(false);
  }, []);

  const value = useMemo(() => ({
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
    user,
    login,
    logout,
  }), [isAuthModalOpen, openAuthModal, closeAuthModal, user, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
